import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart3, TrendingUp, Package, Users, DollarSign, FileText,
  Calendar, AlertTriangle, Award, Wallet, ShoppingBag, Clock, Layers, Briefcase, AlertCircle,
  Eye, KeyRound, UserCheck, Warehouse
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';
import api from '../../utils/api';
import { toast } from 'sonner';
import InventoryStats from '../../components/InventoryStats';

const RANGES = [
  { id: 'today', label: 'Hoy' },
  { id: 'week', label: '7 días' },
  { id: 'month', label: '30 días' },
  { id: 'year', label: '1 año' },
  { id: 'all', label: 'Todo' },
];

const COLORS = {
  primary: '#A855F7',      // morado
  emerald: '#10B981',      // verde
  amber: '#F59E0B',        // ámbar
  rose: '#F43F5E',         // rosa
  blue: '#3B82F6',         // azul
  cyan: '#06B6D4',         // cian
};
const PIE_COLORS = ['#A855F7', '#10B981', '#F59E0B', '#3B82F6', '#06B6D4', '#F43F5E', '#8B5CF6', '#84CC16'];

const fmtMoney = (n) => `$${(n || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`;
const fmtNum = (n) => (n || 0).toLocaleString('es-CO');

const KPICard = ({ icon: Icon, label, value, sub, color = 'primary', testid }) => (
  <div data-testid={testid} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 sm:p-5 hover:border-white/20 transition-all duration-300 hover:-translate-y-0.5">
    <div className="flex items-start justify-between mb-3">
      <div className={`p-2 rounded-md`} style={{ backgroundColor: `${COLORS[color]}20` }}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: COLORS[color] }} />
      </div>
    </div>
    <p className="text-xs uppercase tracking-wider text-white/40 mb-1">{label}</p>
    <p className="text-lg sm:text-2xl font-semibold text-white break-all">{value}</p>
    {sub && <p className="text-xs text-white/50 mt-1">{sub}</p>}
  </div>
);

const ChartCard = ({ title, subtitle, children, icon: Icon, testid }) => (
  <div data-testid={testid} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 sm:p-6">
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon className="w-4 h-4 text-white/60" />}
      <h3 className="text-sm uppercase tracking-wider text-white/80">{title}</h3>
    </div>
    {subtitle && <p className="text-xs text-white/40 mb-4">{subtitle}</p>}
    <div className="mt-3">{children}</div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-black/90 backdrop-blur-sm border border-white/20 rounded p-2 sm:p-3 text-xs">
      <p className="text-white/60 mb-1">{label}</p>
      {payload.map((entry, idx) => (
        <p key={idx} className="font-medium" style={{ color: entry.color }}>
          {entry.name}: {entry.dataKey === 'units' || entry.dataKey === 'invoices' ? fmtNum(entry.value) : fmtMoney(entry.value)}
        </p>
      ))}
    </div>
  );
};

const AnalyticsPanel = () => {
  const [range, setRange] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview'); // 'overview' | 'inventory'

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/analytics/overview?range=${range}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) {
      toast.error('Error cargando estadísticas');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const seriesData = useMemo(() => (data?.series || []).map(s => ({
    date: s.date,
    Ventas: s.sales,
    Pagos: s.payments,
    Facturas: s.invoices,
  })), [data]);

  const statusData = useMemo(() => {
    if (!data?.billing?.by_status) return [];
    const m = data.billing.by_status;
    return [
      { name: 'Pendientes', value: m.pendiente || 0, color: COLORS.amber },
      { name: 'Parciales', value: m.parcial || 0, color: COLORS.blue },
      { name: 'Pagadas', value: m.pagada || 0, color: COLORS.emerald },
      { name: 'Anuladas', value: m.anulada || 0, color: COLORS.rose },
    ].filter(x => x.value > 0);
  }, [data]);

  const methodData = useMemo(() => {
    if (!data?.billing?.by_method) return [];
    return Object.entries(data.billing.by_method).map(([k, v]) => ({
      name: k.charAt(0).toUpperCase() + k.slice(1),
      value: v
    }));
  }, [data]);

  const warehouseData = useMemo(() => {
    if (!data?.inventory?.warehouses) return [];
    const labels = { china: 'China', contenedor: 'Contenedor', stock: 'Stock', sin_stock: 'Sin Stock', archivados: 'Archivados' };
    return Object.entries(data.inventory.warehouses)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => ({ name: labels[k] || k, value: v }));
  }, [data]);

  if (loading && !data && activeView === 'overview') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white/60 text-sm uppercase tracking-wider">Cargando estadísticas...</div>
      </div>
    );
  }

  // Si el usuario va a la pestaña Inventario detallado, mostramos el componente independiente
  if (activeView === 'inventory') {
    return (
      <div className="space-y-6" data-testid="analytics-panel">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-white">Estadísticas</h2>
            <p className="text-xs text-white/50 mt-1">Capital invertido por bodega y categoría</p>
          </div>
          <ViewSwitcher activeView={activeView} setActiveView={setActiveView} />
        </div>
        <InventoryStats />
      </div>
    );
  }

  if (!data) return null;

  const b = data.billing;
  const inv = data.inventory;
  const cap = data.capital || {};
  const collectionRate = b.total_sales > 0 ? ((b.total_paid / b.total_sales) * 100).toFixed(1) : 0;
  
  // Composición del capital total para barra de progreso
  const totalCap = cap.total_invested || 1;
  const invShare = ((cap.inventory_at_cost || cap.inventory_at_retail || 0) / totalCap * 100).toFixed(1);
  const arShare = ((cap.accounts_receivable || 0) / totalCap * 100).toFixed(1);

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="analytics-panel">
      {/* Header con filtros temporales */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-white">Estadísticas</h2>
          <p className="text-xs text-white/50 mt-1">Análisis consolidado de inventario y facturación</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <ViewSwitcher activeView={activeView} setActiveView={setActiveView} />
          <div className="flex gap-1 sm:gap-2 overflow-x-auto pb-2 sm:pb-0 -mx-1 px-1">
            {RANGES.map(r => (
              <button
                key={r.id}
                data-testid={`range-${r.id}`}
              onClick={() => setRange(r.id)}
              className={`px-3 sm:px-4 py-2 text-xs uppercase tracking-wider whitespace-nowrap rounded-md transition-all duration-200 ${
                range === r.id
                  ? 'bg-white text-black shadow-lg shadow-white/10'
                  : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/10'
              }`}
            >
              <Calendar className="w-3 h-3 inline mr-1.5" />
              {r.label}
            </button>
          ))}
          </div>
        </div>
      </div>

      {/* CAPITAL TOTAL INVERTIDO — Hero card */}
      <div data-testid="capital-hero" className="relative overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-purple-950/40 via-[#0A0A0A] to-emerald-950/30 p-5 sm:p-7">
        <div className="absolute top-0 right-0 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-purple-400" />
              <span className="text-xs uppercase tracking-[0.2em] text-white/50">Capital Total Invertido</span>
            </div>
            <span className="text-xs text-white/40 hidden sm:inline">Siempre a precio de costo</span>
          </div>
          <p data-testid="capital-total" className="text-3xl sm:text-5xl font-bold text-white mb-3 break-all">
            {fmtMoney(cap.total_invested)}
          </p>
          <p className="text-xs text-white/50 mb-5">
            Inventario a costo + Cuentas por cobrar
          </p>

          {/* Barra de composición */}
          <div className="h-3 bg-white/5 rounded-full overflow-hidden flex mb-3">
            <div className="bg-gradient-to-r from-purple-500 to-purple-400 h-full transition-all duration-500" style={{ width: `${invShare}%` }} title={`Inventario a costo: ${invShare}%`} />
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-400 h-full transition-all duration-500" style={{ width: `${arShare}%` }} title={`Por cobrar: ${arShare}%`} />
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 text-xs">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-sm bg-purple-500" />
              <span className="text-white/70">Inventario (costo):</span>
              <span className="text-white font-medium">{fmtMoney(cap.inventory_at_cost)}</span>
              <span className="text-white/40">({invShare}%)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
              <span className="text-white/70">Por cobrar:</span>
              <span className="text-white font-medium">{fmtMoney(cap.accounts_receivable)}</span>
              <span className="text-white/40">({arShare}%)</span>
            </div>
          </div>

          {/* Sub-KPIs del capital */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mt-5 pt-5 border-t border-white/10">
            <div data-testid="capital-inv-retail">
              <p className="text-[10px] uppercase tracking-wider text-white/40">Valor venta inventario</p>
              <p className="text-sm sm:text-base font-semibold text-white mt-0.5">{fmtMoney(cap.inventory_at_retail)}</p>
            </div>
            <div data-testid="capital-margin">
              <p className="text-[10px] uppercase tracking-wider text-white/40">Margen potencial</p>
              <p className="text-sm sm:text-base font-semibold text-emerald-400 mt-0.5">
                {cap.potential_margin > 0 ? fmtMoney(cap.potential_margin) : '—'}
              </p>
            </div>
            <div data-testid="capital-collection">
              <p className="text-[10px] uppercase tracking-wider text-white/40">Tasa de cobro</p>
              <p className="text-sm sm:text-base font-semibold text-cyan-400 mt-0.5">{cap.collection_rate}%</p>
            </div>
            <div data-testid="capital-debtors">
              <p className="text-[10px] uppercase tracking-wider text-white/40">Clientes con deuda</p>
              <p className="text-sm sm:text-base font-semibold text-amber-400 mt-0.5">{cap.clients_with_debt}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Warning: productos sin precio de costo */}
      {cap.products_without_cost_count > 0 && (
        <div data-testid="warning-no-cost" className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-200">
              {cap.products_without_cost_count} producto{cap.products_without_cost_count !== 1 ? 's' : ''} sin precio de costo registrado ({fmtNum(cap.units_without_cost)} unidades)
            </p>
            <p className="text-xs text-amber-200/70 mt-1">
              El capital invertido en estos productos no se está contando. Para una cifra exacta, asígnales un precio de costo desde el panel de productos.
            </p>
            {cap.products_without_cost_sample && cap.products_without_cost_sample.length > 0 && (
              <details className="mt-2">
                <summary className="text-xs text-amber-300 cursor-pointer hover:text-amber-200 select-none">Ver ejemplos ({cap.products_without_cost_sample.length})</summary>
                <ul className="mt-2 space-y-1 text-xs text-white/60">
                  {cap.products_without_cost_sample.map((p, i) => (
                    <li key={i} className="flex justify-between gap-3">
                      <span className="truncate">{p.name}</span>
                      <span className="text-amber-300/80 whitespace-nowrap">{fmtNum(p.units)} uds</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>
      )}

      {/* KPIs del rango seleccionado */}
      <div>
        <p className="text-xs uppercase tracking-wider text-white/40 mb-3 flex items-center gap-2">
          <Clock className="w-3 h-3" />
          Actividad en el período: {RANGES.find(r => r.id === range)?.label}
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <KPICard testid="kpi-sales" icon={DollarSign} label="Ventas" value={fmtMoney(b.total_sales)}
            sub={`${b.invoices_count} facturas`} color="primary" />
          <KPICard testid="kpi-collected" icon={Wallet} label="Cobrado" value={fmtMoney(b.total_paid)}
            sub={`${collectionRate}% del total`} color="emerald" />
          <KPICard testid="kpi-pending" icon={Clock} label="Pendiente cobro" value={fmtMoney(b.total_pending)}
            sub={`${b.by_status.pendiente + b.by_status.parcial} fact.`} color="amber" />
          <KPICard testid="kpi-payments" icon={TrendingUp} label="Pagos recibidos" value={fmtMoney(b.payments_total)}
            sub={`${b.payments_count} transacciones`} color="cyan" />
        </div>
      </div>

      {/* Inventario KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KPICard testid="kpi-products" icon={Package} label="Productos" value={fmtNum(inv.total_products)} color="blue" />
        <KPICard testid="kpi-units" icon={ShoppingBag} label="Unidades stock" value={fmtNum(inv.total_units)}
          sub={`Stock + Contenedor + China`} color="primary" />
        <KPICard testid="kpi-inventory-value" icon={DollarSign} label="Inv. a costo" value={fmtMoney(inv.total_value_cost)}
          sub="Capital real en mercancía" color="emerald" />
        <KPICard testid="kpi-clients" icon={Users} label="Clientes" value={fmtNum(data.total_clients)} color="rose" />
      </div>

      {/* Desglose por bodega: unidades + valor a COSTO */}
      <ChartCard testid="chart-warehouses-money" icon={Layers} title="Capital por Bodega" subtitle="Valor a precio de costo (capital invertido en cada bodega)">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { id: 'china', name: 'China', icon: '🚢' },
            { id: 'contenedor', name: 'Contenedor', icon: '📦' },
            { id: 'stock', name: 'Stock', icon: '🏬' },
            { id: 'sin_stock', name: 'Sin Stock', icon: '⚠️' },
            { id: 'archivados', name: 'Archivados', icon: '📁' },
          ].map(wh => {
            const units = inv.warehouses?.[wh.id] || 0;
            const cost = inv.warehouses_cost?.[wh.id] || 0;
            return (
              <div key={wh.id} data-testid={`warehouse-${wh.id}`} className="bg-white/[0.03] border border-white/10 rounded-lg p-3 hover:border-white/20 hover:-translate-y-0.5 transition-all duration-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wider text-white/50">{wh.name}</span>
                  <span className="text-base">{wh.icon}</span>
                </div>
                <p className="text-base sm:text-lg font-bold text-white break-all">{fmtMoney(cost)}</p>
                <p className="text-xs text-white/50 mt-1">{fmtNum(units)} unidades</p>
              </div>
            );
          })}
        </div>
      </ChartCard>

      {/* Gráfico principal: evolución temporal */}
      <ChartCard
        testid="chart-evolution"
        icon={TrendingUp}
        title="Evolución de Ventas y Pagos"
        subtitle={`Granularidad: ${data.granularity === 'hour' ? 'por hora' : data.granularity === 'day' ? 'por día' : 'por mes'}`}
      >
        {seriesData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-white/40 text-sm">Sin datos en este rango</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={seriesData}>
              <defs>
                <linearGradient id="ventas" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={COLORS.primary} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="pagos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLORS.emerald} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={COLORS.emerald} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="date" stroke="#ffffff60" fontSize={10} tick={{ fill: '#ffffff80' }} />
              <YAxis stroke="#ffffff60" fontSize={10} tick={{ fill: '#ffffff80' }}
                tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ color: '#ffffff80', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }} />
              <Area type="monotone" dataKey="Ventas" stroke={COLORS.primary} strokeWidth={2} fill="url(#ventas)" />
              <Area type="monotone" dataKey="Pagos" stroke={COLORS.emerald} strokeWidth={2} fill="url(#pagos)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* Grid 2 columnas: Status + Método de pago */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <ChartCard testid="chart-status" icon={FileText} title="Facturas por Estado">
          {statusData.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-white/40 text-sm">Sin facturas</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45}
                  label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                  {statusData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: '#000', border: '1px solid #ffffff20', borderRadius: '4px', fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard testid="chart-methods" icon={Wallet} title="Pagos por Método">
          {methodData.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-white/40 text-sm">Sin pagos</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={methodData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                  {methodData.map((entry, idx) => (
                    <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmtMoney(v)}
                  contentStyle={{ background: '#000', border: '1px solid #ffffff20', borderRadius: '4px', fontSize: '12px' }} />
                <Legend wrapperStyle={{ fontSize: '11px', color: '#ffffff80' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Inventario: bodegas + categorías + marcas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <ChartCard testid="chart-warehouses" icon={Package} title="Distribución por Bodega" subtitle="Unidades disponibles">
          {warehouseData.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-white/40 text-sm">Sin inventario</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={warehouseData} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" horizontal={false} />
                <XAxis type="number" stroke="#ffffff60" fontSize={10} tick={{ fill: '#ffffff80' }} />
                <YAxis type="category" dataKey="name" stroke="#ffffff60" fontSize={11} tick={{ fill: '#ffffff80' }} width={80} />
                <Tooltip formatter={(v) => fmtNum(v)}
                  contentStyle={{ background: '#000', border: '1px solid #ffffff20', borderRadius: '4px', fontSize: '12px' }} />
                <Bar dataKey="value" fill={COLORS.primary} radius={[0, 4, 4, 0]}>
                  {warehouseData.map((entry, idx) => (
                    <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard testid="chart-brands" icon={Award} title="Top Marcas por Unidades">
          {(inv.by_brand || []).length === 0 ? (
            <div className="h-56 flex items-center justify-center text-white/40 text-sm">Sin datos</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={inv.by_brand.slice(0, 8)} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" horizontal={false} />
                <XAxis type="number" stroke="#ffffff60" fontSize={10} tick={{ fill: '#ffffff80' }} />
                <YAxis type="category" dataKey="name" stroke="#ffffff60" fontSize={11} tick={{ fill: '#ffffff80' }} width={110} />
                <Tooltip formatter={(v) => fmtNum(v)}
                  contentStyle={{ background: '#000', border: '1px solid #ffffff20', borderRadius: '4px', fontSize: '12px' }} />
                <Bar dataKey="units" fill={COLORS.cyan} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Top productos + Top clientes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <ChartCard testid="chart-top-products" icon={ShoppingBag} title="Top Productos Vendidos" subtitle="En el rango seleccionado">
          {(data.top_products || []).length === 0 ? (
            <div className="h-40 flex items-center justify-center text-white/40 text-sm">Sin ventas</div>
          ) : (
            <div className="space-y-2">
              {data.top_products.slice(0, 8).map((p, idx) => (
                <div key={p.product_id} className="flex items-center gap-3 p-2 hover:bg-white/5 rounded transition-colors">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0"
                    style={{ backgroundColor: `${PIE_COLORS[idx % PIE_COLORS.length]}30`, color: PIE_COLORS[idx % PIE_COLORS.length] }}>
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{p.product_name}</p>
                    <p className="text-xs text-white/40">{fmtNum(p.quantity)} uds · {fmtMoney(p.revenue)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ChartCard>

        <ChartCard testid="chart-top-debtors" icon={AlertCircle} title="Top Clientes con Deuda" subtitle="Total acumulado por cobrar">
          {(data.top_debtors || []).length === 0 ? (
            <div className="h-40 flex items-center justify-center text-emerald-400 text-sm">✓ Ningún cliente con deuda</div>
          ) : (
            <div className="space-y-2">
              {data.top_debtors.slice(0, 8).map((c, idx) => (
                <div key={c.client_id} className="flex items-center gap-3 p-2 hover:bg-white/5 rounded transition-colors">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 bg-amber-500/20 text-amber-400">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{c.client_name}</p>
                    <p className="text-xs text-white/40">{c.invoices_pending} fact. pendiente{c.invoices_pending > 1 ? 's' : ''}</p>
                  </div>
                  <span className="text-sm font-semibold text-amber-400 whitespace-nowrap">{fmtMoney(c.debt)}</span>
                </div>
              ))}
            </div>
          )}
        </ChartCard>
      </div>

      {/* Top clientes (compradores) */}
      <ChartCard testid="chart-top-clients" icon={Users} title="Top Clientes Compradores" subtitle="Por monto total de compra en el rango">
        {(data.top_clients || []).length === 0 ? (
          <div className="h-40 flex items-center justify-center text-white/40 text-sm">Sin clientes activos</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.top_clients.slice(0, 10).map((c, idx) => (
              <div key={c.client_id} className="flex items-center gap-3 p-2 hover:bg-white/5 rounded transition-colors">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 bg-white/10 text-white/80">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{c.client_name}</p>
                  <p className="text-xs text-white/40">{c.invoices_count} fact · {fmtMoney(c.total_purchases)}</p>
                </div>
                {c.total_pending > 0 && (
                  <span className="text-xs text-amber-400 whitespace-nowrap">{fmtMoney(c.total_pending)} ⏳</span>
                )}
              </div>
            ))}
          </div>
        )}
      </ChartCard>

      {/* Alertas: low stock */}
      {(inv.low_stock || []).length > 0 && (
        <ChartCard testid="chart-low-stock" icon={AlertTriangle} title="Alertas de Stock Bajo" subtitle="Productos con 5 o menos unidades">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {inv.low_stock.map((p, idx) => (
              <div key={idx} className="flex items-center justify-between gap-2 p-2 bg-amber-500/5 border border-amber-500/20 rounded">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{p.name}</p>
                  <p className="text-xs text-white/50 truncate">{p.brand}</p>
                </div>
                <span className="text-amber-400 font-semibold text-sm">{p.stock} uds</span>
              </div>
            ))}
          </div>
        </ChartCard>
      )}

      {/* Sección: Ingresos a la página */}
      <AccessStatsSection />
    </div>
  );
};

const ACCESS_RANGES = [
  { id: 'day', label: 'Hoy' },
  { id: 'week', label: 'Semana' },
  { id: 'month', label: 'Mes' },
  { id: 'year', label: 'Año' },
  { id: 'all', label: 'Todo' },
];

const AccessStatsSection = () => {
  const [range, setRange] = useState('month');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/analytics/access-stats?range=${range}`);
        setStats(res.data);
      } catch (e) {
        toast.error('Error cargando estadísticas de ingresos');
      } finally {
        setLoading(false);
      }
    })();
  }, [range]);

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  return (
    <div className="mt-10 pt-8 border-t border-white/10" data-testid="access-stats-section">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold mb-1 flex items-center gap-2">
            <Eye className="w-5 h-5 text-purple-400" />
            Ingresos a la página
          </h2>
          <p className="text-xs text-white/50">Visitas al catálogo público + ingresos de clientes al portal con código</p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="access-range-filters">
          {ACCESS_RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              data-testid={`access-range-${r.id}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-wider border transition-colors ${
                range === r.id ? 'bg-white text-black border-white' : 'border-white/15 text-white/60 hover:border-white/40 hover:text-white'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-center text-white/40 py-10 text-sm">Cargando...</p>
      ) : !stats ? (
        <p className="text-center text-white/40 py-10 text-sm">Sin datos.</p>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
            <KPICard
              icon={Eye}
              label="Visitas al catálogo"
              value={fmtNum(stats.catalog_views)}
              sub="Visitantes anónimos (sin código)"
              color="primary"
              testid="kpi-catalog-views"
            />
            <KPICard
              icon={KeyRound}
              label="Ingresos al portal"
              value={fmtNum(stats.portal_logins)}
              sub="Total de accesos con código"
              color="emerald"
              testid="kpi-portal-logins"
            />
            <KPICard
              icon={UserCheck}
              label="Clientes únicos"
              value={fmtNum(stats.unique_clients)}
              sub="Clientes distintos que ingresaron"
              color="cyan"
              testid="kpi-unique-clients"
            />
          </div>

          {/* Por cliente */}
          <ChartCard testid="chart-logins-by-client" icon={Users} title="Ingresos por cliente" subtitle={`Lista ordenada por número de accesos en el período · ${stats.by_client.length} cliente(s)`}>
            {stats.by_client.length === 0 ? (
              <p className="text-sm text-white/40 py-6 text-center">Ningún cliente ha ingresado en este período.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-white/10">
                      <th className="py-2 px-2 text-[10px] uppercase tracking-wider text-white/40 font-semibold">Cliente</th>
                      <th className="py-2 px-2 text-[10px] uppercase tracking-wider text-white/40 font-semibold">Código</th>
                      <th className="py-2 px-2 text-[10px] uppercase tracking-wider text-white/40 font-semibold text-right">Ingresos</th>
                      <th className="py-2 px-2 text-[10px] uppercase tracking-wider text-white/40 font-semibold text-right">Último acceso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.by_client.map((c) => (
                      <tr key={c.client_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="py-3 px-2 text-white truncate max-w-[200px]">{c.client_name || '—'}</td>
                        <td className="py-3 px-2">
                          {c.client_code && (
                            <span className="text-xs font-mono tracking-widest bg-white/10 border border-white/20 px-2 py-0.5">{c.client_code}</span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-right">
                          <span className="inline-flex items-center gap-1.5">
                            <KeyRound className="w-3 h-3 text-emerald-400" />
                            <span className="font-semibold text-emerald-400 text-base tabular-nums">{c.logins}</span>
                          </span>
                        </td>
                        <td className="py-3 px-2 text-right text-white/60 text-xs">{formatDateTime(c.last_login)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>
        </>
      )}
    </div>
  );
};

export default AnalyticsPanel;

const ViewSwitcher = ({ activeView, setActiveView }) => (
  <div className="flex gap-1 bg-[#0A0A0A] border border-white/10 p-1 rounded-md" data-testid="analytics-view-switcher">
    <button
      type="button"
      onClick={() => setActiveView('overview')}
      data-testid="analytics-view-overview"
      className={`px-3 py-1.5 text-[10px] uppercase tracking-wider transition-colors flex items-center gap-1.5 rounded ${
        activeView === 'overview' ? 'bg-white text-black' : 'text-white/50 hover:text-white'
      }`}
    >
      <BarChart3 className="w-3.5 h-3.5" />
      Resumen
    </button>
    <button
      type="button"
      onClick={() => setActiveView('inventory')}
      data-testid="view-inventory-stats"
      className={`px-3 py-1.5 text-[10px] uppercase tracking-wider transition-colors flex items-center gap-1.5 rounded ${
        activeView === 'inventory' ? 'bg-white text-black' : 'text-white/50 hover:text-white'
      }`}
    >
      <Warehouse className="w-3.5 h-3.5" />
      Inventario
    </button>
  </div>
);
